import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { fetchAuthSession, signInWithRedirect, signOut as amplifySignOut } from 'aws-amplify/auth';
import { User } from '../Types/User';

/** Route that hosts the post-logout landing page (public, no login redirect). */
export const SIGNOUT_PATH = '/signout';

/**
 * sessionStorage marker set when the user intentionally signs out. The /signout
 * landing page reads it to decide whether to also end the federated IdP (Azure AD)
 * session when REACT_APP_IDP_LOGOUT_URL is configured.
 */
export const SIGNOUT_PENDING_KEY = 'eeas.signout.pending';

const readStringClaim = (payload: Record<string, unknown>, claimNames: string[]): string => {
  for (const claimName of claimNames) {
    const claimValue = payload[claimName];
    if (typeof claimValue === 'string' && claimValue.trim()) {
      return claimValue.trim();
    }
  }

  return '';
};

const buildDisplayName = (payload: Record<string, unknown>): string => {
  const name = readStringClaim(payload, ['name', 'preferred_username', 'cognito:username', 'email']);
  if (name) {
    return name;
  }

  const givenName = readStringClaim(payload, ['given_name']);
  const familyName = readStringClaim(payload, ['family_name']);
  return [givenName, familyName].filter(Boolean).join(' ');
};

const buildEmail = (payload: Record<string, unknown>): string => {
  return readStringClaim(payload, ['email', 'preferred_username', 'upn']);
};

type AuthContextType = {
  user: User | null;
  isSignedIn: boolean;
  initialized: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const signIn = useCallback(async () => {
    setInitialized(true);
    await signInWithRedirect({ provider: { custom: process.env.REACT_APP_AUTH_PROVIDER! } });
  }, []);

  const signOut = useCallback(async () => {
    // Defensively clear local auth + cached security/roster/incident state before
    // handing off to the Cognito Hosted UI logout flow.
    setUser(null);
    setIsSignedIn(false);
    queryClient.clear();
    try {
      // Signal the /signout landing page to also end the federated IdP (Azure AD)
      // session when REACT_APP_IDP_LOGOUT_URL is configured.
      window.sessionStorage.setItem(SIGNOUT_PENDING_KEY, '1');
    } catch {
      /* sessionStorage may be unavailable (private mode) — IdP chaining is best-effort */
    }
    try {
      // Amplify signOut redirects through the Cognito Hosted UI logout endpoint and
      // back to redirectSignOut (REACT_APP_REDIRECT_SIGNOUT) configured in amplifyConfig.
      await amplifySignOut();
    } catch (error) {
      console.error('Error during sign out:', error);
      // Fall back to the local signed-out landing page so the app never remains
      // in a half-authenticated state.
      navigate(SIGNOUT_PATH, { replace: true });
    }
  }, [queryClient, navigate]);

  const authenticate = useCallback(async () => {
    // The post-logout landing page is public and must never trigger a login redirect.
    if (window.location.pathname === SIGNOUT_PATH) {
      setInitialized(true);
      return;
    }
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken.toString();
      const userPayload = session.tokens?.idToken?.payload;
      if (token && userPayload) {
        const payload = userPayload as Record<string, unknown>;
        setUser({
          displayName: buildDisplayName(payload),
          email: buildEmail(payload),
          roles: [],
        });
        setIsSignedIn(true);
      } else {
        await signIn();
      }
    } catch {
      await signIn();
    } finally {
      setInitialized(true);
    }
  }, [signIn]);

  useEffect(() => {
    authenticate();
  }, [authenticate]);

  return (
    <AuthContext.Provider value={{ user, isSignedIn, initialized, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
